/* eslint-disable complexity */

import {AxialBalanceSignals, StationParts} from '@domain/kiln-monitoring'
import {customAlphabet} from 'nanoid'

import {
  calculateXForAssetPart,
  roundTo,
  getAllSelectedSignals,
  getKilnShellDimensions
} from '../../../helpers'
import {KilnConfigActionType, KilnConfigAction} from '../../../types'
import {INITIAL_LAYOUT, STATION, DRIVE} from '../constants'
import type {KilnConfigState} from '../types'

const nanoid = customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', 6)

export const kilnConfigReducer = (
  state: KilnConfigState,
  action: KilnConfigAction
): KilnConfigState => {
  const {layout, configuration} = state

  switch (action.type) {
    case KilnConfigActionType.CHANGE_ROTATION:
      return {
        ...state,
        layout: {...layout, isRotationClockwise: action.payload}
      }
    case KilnConfigActionType.ADD_THRUST_ROLLER:
      return {
        ...state,
        layout: {
          ...layout,
          stations: layout.stations.map((station) =>
            station.id === action.payload.id
              ? {
                  ...station,
                  thrustRollerPosition: action.payload.position,
                  width: STATION.WIDTH_TR,
                  x: station.x - 20
                }
              : station
          )
        }
      }
    case KilnConfigActionType.REMOVE_THRUST_ROLLER:
      return {
        ...state,
        layout: {
          ...layout,
          stations: layout.stations.map((station) =>
            station.id === action.payload.id
              ? {
                  ...station,
                  thrustRollerPosition: 'none',
                  width: STATION.WIDTH_NO_TR,
                  x: station.x + 20
                }
              : station
          ),
          selectedSignals: Object.fromEntries(
            Object.entries(layout.selectedSignals).filter(
              ([key]) => key !== `${action.payload.id}_${StationParts.THRUST_ROLLER}`
            )
          )
        }
      }
    case KilnConfigActionType.SET_DUAL_DRIVE:
      return {
        ...state,
        layout: {
          ...layout,
          interactiveOverlayHeight: DRIVE.OVERLAY_HEIGHT_DUAL,
          referenceDimensions: {...layout.referenceDimensions, height: DRIVE.HEIGHT_DUAL},
          kilnShell: {
            ...layout.kilnShell,
            y: INITIAL_LAYOUT.kilnShell.y + DRIVE.Y_OFFSET_TOP
          },
          stations: layout.stations.map((station) => ({
            ...station,
            y: DRIVE.Y_OFFSET_TOP
          })),
          drive: {
            ...layout.drive,
            position: 'double',
            height: DRIVE.HEIGHT_DUAL,
            y: 0
          }
        }
      }
    case KilnConfigActionType.SET_BOTTOM_DRIVE:
      return {
        ...state,
        layout: {
          ...layout,
          interactiveOverlayHeight: DRIVE.OVERLAY_HEIGHT_SINGLE,
          referenceDimensions: {
            ...layout.referenceDimensions,
            height: INITIAL_LAYOUT.referenceDimensions.height
          },
          kilnShell: {...layout.kilnShell, y: INITIAL_LAYOUT.kilnShell.y},
          stations: layout.stations.map((station) => ({
            ...station,
            y: 0
          })),
          drive: {
            ...layout.drive,
            position: 'bottom',
            height: DRIVE.HEIGHT_SINGLE,
            y: DRIVE.Y_SINGLE
          },
          selectedSignals: Object.fromEntries(
            Object.entries(layout.selectedSignals).filter(
              ([key]) => !key.startsWith(layout.drive.id)
            )
          )
        }
      }
    case KilnConfigActionType.SET_TOP_DRIVE:
      return {
        ...state,
        layout: {
          ...layout,
          interactiveOverlayHeight: DRIVE.OVERLAY_HEIGHT_SINGLE,
          referenceDimensions: {...layout.referenceDimensions, height: DRIVE.HEIGHT_DUAL},
          kilnShell: {
            ...layout.kilnShell,
            y: INITIAL_LAYOUT.kilnShell.y + DRIVE.Y_OFFSET_TOP
          },
          stations: layout.stations.map((station) => ({
            ...station,
            y: DRIVE.Y_OFFSET_TOP
          })),
          drive: {
            ...layout.drive,
            position: 'top',
            height: DRIVE.HEIGHT_SINGLE,
            y: 0
          },
          selectedSignals: Object.fromEntries(
            Object.entries(layout.selectedSignals).filter(
              ([key]) => !key.startsWith(layout.drive.id)
            )
          )
        }
      }
    case KilnConfigActionType.ADD_STATION:
      return {
        ...state,
        layout: {
          ...layout,
          stations: [
            ...layout.stations,
            {
              id: `station-${nanoid()}`,
              x: calculateXForAssetPart({
                draggableElements: [
                  layout.drive,
                  ...layout.stations,
                  {
                    id: 'dummy',
                    x: layout.referenceDimensions.width,
                    y: 0,
                    width: 0,
                    height: 0
                  }
                ],
                assetPartTotalWidth: layout.stationOverlayWidth
              }),
              y: layout.drive.position === 'bottom' ? 0 : DRIVE.Y_OFFSET_TOP,
              width: STATION.WIDTH_NO_TR,
              height: STATION.HEIGHT,
              thrustRollerPosition: 'none',
              dimensions: {distanceFrom0: undefined, width: undefined}
            }
          ]
        }
      }
    case KilnConfigActionType.REMOVE_STATION:
      return {
        ...state,
        layout: {
          ...layout,
          stations: layout.stations.filter((station) => station.id !== action.payload.id),
          selectedSignals: Object.fromEntries(
            Object.entries(layout.selectedSignals).filter(
              ([key]) => !key.startsWith(action.payload.id)
            )
          ),
          axialBalanceSignals: Array.isArray(layout.axialBalanceSignals)
            ? {}
            : Object.fromEntries(
                Object.entries(layout.axialBalanceSignals).filter(
                  ([key]) => !key.startsWith(action.payload.id)
                )
              )
        }
      }
    case KilnConfigActionType.SET_SELECTED_SIGNALS:
      return {
        ...state,
        layout: {
          ...layout,
          selectedSignals: action.payload
        }
      }
    case KilnConfigActionType.FLIP_MATERIAL_FLOW:
      return {
        ...state,
        layout: {
          ...layout,
          materialFlow: layout.materialFlow === 'leftToRight' ? 'rightToLeft' : 'leftToRight'
        }
      }
    case KilnConfigActionType.MOVE_X: {
      const {id, deltaX} = action.payload
      const {metersInPixel} = getKilnShellDimensions(configuration, layout)

      return {
        ...state,
        layout: {
          ...layout,
          stations: layout.stations.map((station) =>
            station.id === id
              ? {
                  ...station,
                  x: station.x + deltaX,
                  dimensions: {
                    ...station.dimensions,
                    distanceFrom0: roundTo((station.x + deltaX) * metersInPixel)
                  }
                }
              : station
          ),
          drive:
            id === layout.drive.id
              ? {
                  ...layout.drive,
                  x: layout.drive.x + deltaX,
                  dimensions: {
                    ...layout.drive.dimensions,
                    distanceFrom0: roundTo((layout.drive.x + deltaX) * metersInPixel)
                  }
                }
              : layout.drive,
          kilnShell: {
            ...layout.kilnShell,
            cones: layout.kilnShell.cones?.map((cone) =>
              id === cone.id
                ? {
                    ...cone,
                    dimensions: {
                      ...cone.dimensions,
                      distanceFrom0: roundTo((cone.x + deltaX) * metersInPixel)
                    }
                  }
                : cone
            )
          }
        }
      }
    }
    case KilnConfigActionType.SET_KILN_PART_WIDTH: {
      const {id, width} = action.payload
      return {
        ...state,
        layout: {
          ...layout,
          stations: layout.stations.map((station) =>
            station.id === id
              ? {
                  ...station,
                  dimensions: {...station.dimensions, width}
                }
              : station
          ),
          drive:
            id === layout.drive.id
              ? {
                  ...layout.drive,
                  dimensions: {...layout.drive.dimensions, width}
                }
              : layout.drive,
          kilnShell: {
            ...layout.kilnShell,
            cones: layout.kilnShell.cones?.map((cone) =>
              id === cone.id
                ? {
                    ...cone,
                    dimensions: {...cone.dimensions, width}
                  }
                : cone
            )
          }
        }
      }
    }
    case KilnConfigActionType.SET_KILN_PART_DIAMETER: {
      const {id, diameter} = action.payload
      return {
        ...state,
        layout: {
          ...layout,
          stations: layout.stations.map((station) =>
            station.id === id
              ? {
                  ...station,
                  dimensions: {...station.dimensions, diameter}
                }
              : station
          ),
          drive:
            id === layout.drive.id
              ? {
                  ...layout.drive,
                  dimensions: {...layout.drive.dimensions, diameter}
                }
              : layout.drive,
          kilnShell: {
            ...layout.kilnShell,
            cones: layout.kilnShell.cones?.map((cone) =>
              id === cone.id
                ? {
                    ...cone,
                    dimensions: {...cone.dimensions, diameter}
                  }
                : cone
            )
          }
        }
      }
    }
    case KilnConfigActionType.SET_STATION_NAME:
      return {
        ...state,
        layout: {
          ...layout,
          stations: layout.stations.map((station) =>
            station.id === action.payload.id
              ? {
                  ...station,
                  name: action.payload.name
                }
              : station
          )
        }
      }
    case KilnConfigActionType.SET_DRIVE_NAME:
      return {
        ...state,
        layout: {...layout, drive: {...layout.drive, name: action.payload}}
      }
    case KilnConfigActionType.SET_SPEED_SIGNALS:
      return {
        ...state,
        layout: {...layout, speedSignals: action.payload}
      }
    case KilnConfigActionType.SET_FEED_SIGNALS:
      return {
        ...state,
        layout: {...layout, feedSignals: action.payload}
      }
    case KilnConfigActionType.SET_OVALITY_SIGNALS:
      return {
        ...state,
        layout: {...layout, ovalitySignals: action.payload}
      }
    case KilnConfigActionType.SET_CRANK_SIGNALS:
      return {
        ...state,
        layout: {...layout, crankSignals: action.payload}
      }
    case KilnConfigActionType.SET_AXIAL_BALANCE_SIGNALS:
      return {
        ...state,
        layout: {...layout, axialBalanceSignals: action.payload}
      }
    case KilnConfigActionType.VERIFY_HEALTH_INDICATORS: {
      const allSignals = getAllSelectedSignals(layout.selectedSignals).map((signal) => signal.id)

      return {
        ...state,
        layout: {
          ...layout,
          speedSignals: layout.speedSignals.filter((signal) => allSignals.includes(signal.id)),
          feedSignals: layout.feedSignals.filter((signal) => allSignals.includes(signal.id)),
          ovalitySignals: layout.ovalitySignals.filter((signal) => allSignals.includes(signal.id)),
          crankSignals: Object.fromEntries(
            Object.entries(layout.crankSignals)
              .map(([key, signals]) => [
                key,
                signals.filter((signal) => allSignals.includes(signal.id))
              ])
              .filter(([, signals]) => signals.length)
          ),
          axialBalanceSignals: Array.isArray(layout.axialBalanceSignals)
            ? {}
            : Object.fromEntries(
                Object.entries(layout.axialBalanceSignals as AxialBalanceSignals | string[]).map(
                  ([key, config]) => [
                    key,
                    {
                      ...config,
                      signal:
                        config.signal && allSignals.includes(config.signal.id)
                          ? config.signal
                          : null
                    }
                  ]
                )
              )
        }
      }
    }
    case KilnConfigActionType.FLIP_THRUST_ROLLER: {
      return {
        ...state,
        layout: {
          ...layout,
          stations: layout.stations.map((station) =>
            station.id === action.payload.id
              ? {
                  ...station,
                  thrustRollerPosition:
                    station.thrustRollerPosition === 'left'
                      ? 'right'
                      : station.thrustRollerPosition === 'right'
                        ? 'left'
                        : station.thrustRollerPosition
                }
              : station
          )
        }
      }
    }
    case KilnConfigActionType.FLIP_MOTOR: {
      const newSide = layout.drive.side === 'left' ? 'right' : 'left'
      const flowBasedSide = layout.materialFlow === 'rightToLeft' ? 'right' : 'left' // for backwards compatibility
      return {
        ...state,
        layout: {
          ...layout,
          drive: {
            ...layout.drive,
            side: layout.drive.side ? newSide : flowBasedSide
          }
        }
      }
    }
    case KilnConfigActionType.SET_KILN_NAME: {
      return {
        ...state,
        configuration: {
          ...configuration,
          name: action.payload
        }
      }
    }
    case KilnConfigActionType.SET_KILN_LENGTH: {
      return {
        ...state,
        configuration: {
          ...configuration,
          dimensions: {
            ...configuration.dimensions,
            length: action.payload
          }
        }
      }
    }
    case KilnConfigActionType.SET_KILN_DIAMETER: {
      return {
        ...state,
        configuration: {
          ...configuration,
          dimensions: {
            ...configuration.dimensions,
            diameter: action.payload
          }
        }
      }
    }
    case KilnConfigActionType.ADD_CONE: {
      const lastCone = layout.kilnShell.cones?.[layout.kilnShell.cones.length - 1]
      return {
        ...state,
        layout: {
          ...layout,
          kilnShell: {
            ...layout.kilnShell,
            cones: [
              ...(layout.kilnShell.cones ?? []),
              {
                id: `cone-${nanoid()}`,
                x: (lastCone?.x ?? 0) + 200,
                y: DRIVE.Y_OFFSET_TOP,
                width: STATION.WIDTH_NO_TR,
                height: STATION.HEIGHT,
                dimensions: {distanceFrom0: undefined, width: undefined, diameter: undefined}
              }
            ]
          }
        }
      }
    }
    case KilnConfigActionType.REMOVE_CONE:
      return {
        ...state,
        layout: {
          ...layout,
          kilnShell: {
            ...layout.kilnShell,
            cones: layout.kilnShell.cones?.filter((cone) => cone.id !== action.payload.id)
          }
        }
      }
    default:
      throw new Error(`Unknown action type: ${action.type}`)
  }
}
